import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { Pin, PinOff, Settings2, X } from 'lucide-react';
import { getSidebarItems } from './config/SidebarItems';
import type { MenuItem, MenuItemView } from './Sidebar';
import { menuLabel, viewLabel } from '../../i18n/labels';
import { useSettings } from '../../contexts/SettingsContext';
import { useAuth } from '../../contexts/AuthContext';
import type { User } from '../../contexts/AuthContext';
import { getFavorites, getCachedFavorites, reconstructFavoriteIcon } from '../../utils/favorites';
import FavoritesManager from './FavoritesManager';
import api from '../../services/api';
import { cn } from '../../lib/utils';
import { Z_INDEX, zStyle } from '../../lib/zLayers';
import { APP_BRAND } from '../../config/branding';
import AppBrandIcon from '../branding/AppBrandIcon';
import UserAvatar, { getUserDisplayName } from '../users/UserAvatar';
import {
  appointmentsCalendarPath,
  isAppointmentsSidebarActive,
  isTodosSidebarActive,
} from '../../modules/crm-appointments/paths';
import { todosSidebarPath } from '../../modules/crm-todos/paths';

const RAIL_WIDTH = 60;
const PANEL_WIDTH = 240;

const PANEL_PIN_KEY = 'app-sidebar-panel-pinned';
const OPEN_SECTION_KEY = 'app-sidebar-open-section';

const viewPreferencesFetcher = async (url: string) => {
  const response = await api.get(url);
  return response.data.data || {};
};

const saveViewPreference = async (moduleName: string, viewType: string, mutate: () => Promise<unknown>) => {
  try {
    await api.patch('/settings/user/view-preferences', { preferences: { [moduleName]: viewType } });
    await mutate();
  } catch (e) {
    console.error('Failed to save view preference:', e);
  }
};

/** Path a menu item should navigate to, honoring special modules and saved view preference. */
function resolveItemPath(item: MenuItem, viewPreferences: Record<string, string>): string | undefined {
  if (item.name === 'Appointments') return appointmentsCalendarPath();
  if (item.name === 'Todos') return todosSidebarPath();
  if (item.views && item.views.length > 0) {
    const saved = viewPreferences[item.name];
    if (saved) {
      if (item.name === 'Emails') return '/emails?view=card';
      const view = item.views.find((v) => v.type === saved);
      if (view) return view.path;
    }
    return item.views[0].path;
  }
  return item.path;
}

function isItemPathActive(item: MenuItem, pathname: string, search: string): boolean {
  if (item.name === 'Appointments') return isAppointmentsSidebarActive(pathname, search);
  if (item.name === 'Todos' && item.path) return isTodosSidebarActive(pathname, search, item.path);
  const candidates: string[] = [];
  if (item.path) candidates.push(item.path);
  item.views?.forEach((v) => candidates.push(v.path));
  return candidates.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function isBranchActive(item: MenuItem, pathname: string, search: string): boolean {
  if (isItemPathActive(item, pathname, search)) return true;
  return (item.children ?? []).some((child) => isBranchActive(child, pathname, search));
}

/** True when the item opens the second panel instead of navigating directly. */
function hasPanelContent(item: MenuItem): boolean {
  if (item.name === 'Favorites') return true;
  if (item.children && item.children.length > 0) return true;
  if (item.views && item.views.length > 1) return true;
  return false;
}

/* ---------- rail ---------- */

interface RailItemProps {
  label: string;
  active: boolean;
  open?: boolean;
  badge?: number;
  onClick: () => void;
  children: React.ReactNode;
}

const RailItem: React.FC<RailItemProps> = ({ label, active, open = false, badge, onClick, children }) => (
  <div className="relative flex w-full justify-center py-0.5">
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        'relative flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-150',
        active
          ? 'bg-indigo-500/25 text-white ring-1 ring-indigo-400/35 shadow-lg shadow-indigo-950/40'
          : open
            ? 'bg-white/[0.1] text-white'
            : 'text-slate-400 hover:bg-white/[0.08] hover:text-slate-100',
        active &&
          'before:absolute before:left-[-0.55rem] before:top-1/2 before:h-5 before:w-[3px] before:-translate-y-1/2 before:rounded-full before:bg-indigo-400',
      )}
    >
      <span className="flex items-center justify-center [&_svg]:h-[1.15rem] [&_svg]:w-[1.15rem]">{children}</span>
    </button>
    {badge ? (
      <span className="pointer-events-none absolute right-1 top-0 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
        {badge > 9 ? '9+' : badge}
      </span>
    ) : null}
  </div>
);

/* ---------- panel tree ---------- */

interface PanelTreeProps {
  items: MenuItem[];
  depth: number;
  pathname: string;
  search: string;
  viewPreferences: Record<string, string>;
  onNavigate: (path: string) => void;
}

const PanelTree: React.FC<PanelTreeProps> = ({ items, depth, pathname, search, viewPreferences, onNavigate }) => {
  const { t } = useTranslation('sidebar');

  return (
    <div className={cn('space-y-0.5', depth > 0 && 'ml-3 border-l border-white/[0.07] pl-2')}>
      {items.map((item, idx) => (
        <PanelTreeNode
          key={item.path ?? `${item.name}-${idx}`}
          item={item}
          depth={depth}
          pathname={pathname}
          search={search}
          viewPreferences={viewPreferences}
          onNavigate={onNavigate}
          t={t}
        />
      ))}
    </div>
  );
};

interface PanelTreeNodeProps extends Omit<PanelTreeProps, 'items'> {
  item: MenuItem;
  t: ReturnType<typeof useTranslation>['t'];
}

const PanelTreeNode: React.FC<PanelTreeNodeProps> = ({ item, depth, pathname, search, viewPreferences, onNavigate, t }) => {
  const hasChildren = Boolean(item.children && item.children.length > 0);
  const branchActive = isBranchActive(item, pathname, search);
  const selfActive = isItemPathActive(item, pathname, search);
  const [expanded, setExpanded] = useState(branchActive || item.defaultExpanded !== false ? branchActive : false);

  useEffect(() => {
    if (branchActive) setExpanded(true);
  }, [branchActive]);

  const label = menuLabel(t, item.name);

  if (hasChildren) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={cn(
            'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
            branchActive ? 'text-white' : 'text-slate-300 hover:bg-white/[0.06] hover:text-white',
          )}
        >
          {item.icon && <span className="flex h-4 w-4 shrink-0 items-center justify-center opacity-80">{item.icon}</span>}
          <span className="flex-1 truncate font-medium">{label}</span>
          <svg
            className={cn('h-3.5 w-3.5 shrink-0 opacity-60 transition-transform', expanded && 'rotate-90')}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        {expanded && (
          <PanelTree
            items={item.children!}
            depth={depth + 1}
            pathname={pathname}
            search={search}
            viewPreferences={viewPreferences}
            onNavigate={onNavigate}
          />
        )}
      </div>
    );
  }

  const target = resolveItemPath(item, viewPreferences);

  return (
    <button
      type="button"
      onClick={() => target && onNavigate(target)}
      className={cn(
        'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
        selfActive
          ? 'bg-indigo-500/20 text-white ring-1 ring-inset ring-indigo-400/25'
          : 'text-slate-400 hover:bg-white/[0.06] hover:text-slate-100',
      )}
    >
      {item.icon && <span className="flex h-4 w-4 shrink-0 items-center justify-center opacity-80">{item.icon}</span>}
      <span className="flex-1 truncate">{label}</span>
      {item.badge ? (
        <span className="shrink-0 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
          {item.badge > 9 ? '9+' : item.badge}
        </span>
      ) : null}
    </button>
  );
};

/* ---------- views list ---------- */

interface PanelViewsProps {
  moduleName: string;
  views: MenuItemView[];
  pathname: string;
  onSelect: (view: MenuItemView) => void;
}

const PanelViews: React.FC<PanelViewsProps> = ({ views, pathname, onSelect }) => {
  const { t } = useTranslation('sidebar');
  return (
    <div className="space-y-0.5">
      <p className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Views</p>
      {views.map((view, idx) => {
        const active = pathname === view.path || pathname.startsWith(`${view.path}/`);
        return (
          <button
            key={idx}
            type="button"
            onClick={() => onSelect(view)}
            className={cn(
              'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
              active
                ? 'bg-indigo-500/20 text-white ring-1 ring-inset ring-indigo-400/25'
                : 'text-slate-400 hover:bg-white/[0.06] hover:text-slate-100',
            )}
          >
            <span className="flex h-4 w-4 shrink-0 items-center justify-center opacity-80">{view.icon}</span>
            <span className="flex-1 truncate">{viewLabel(t, view.label)}</span>
          </button>
        );
      })}
    </div>
  );
};

/* ---------- main component ---------- */

interface TwoTierSidebarProps {
  isMobileOpen: boolean;
  setIsMobileOpen: (open: boolean) => void;
}

const TwoTierSidebar: React.FC<TwoTierSidebarProps> = ({ isMobileOpen, setIsMobileOpen }) => {
  const { t } = useTranslation('sidebar');
  const { appConfig } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, can, permissionsLoaded, logout } = useAuth();

  const [favoritesManagerOpen, setFavoritesManagerOpen] = useState(false);
  const [pinned, setPinned] = useState(() => localStorage.getItem(PANEL_PIN_KEY) !== 'false');
  const [openSection, setOpenSection] = useState<string | null>(() => {
    const saved = localStorage.getItem(OPEN_SECTION_KEY);
    return saved || null;
  });

  const { data: rawViewPreferences, mutate: mutateViewPreferences } = useSWR<Record<string, string>>(
    '/settings/user/view-preferences',
    viewPreferencesFetcher,
    { revalidateOnFocus: true, revalidateOnReconnect: true, revalidateOnMount: true, refreshInterval: 0 },
  );
  const viewPreferences = rawViewPreferences ?? {};

  const canView = useMemo(
    () => (permissionsLoaded || user?.role === 'ADMIN' ? (moduleKey: string) => can(moduleKey, 'view') : undefined),
    [can, permissionsLoaded, user?.role],
  );
  const canEdit = useMemo(
    () => (permissionsLoaded || user?.role === 'ADMIN' ? (moduleKey: string) => can(moduleKey, 'edit') : undefined),
    [can, permissionsLoaded, user?.role],
  );
  const baseMenuItems = useMemo(() => getSidebarItems(user?.role, canView, canEdit), [user?.role, canView, canEdit]);
  const baseMenuItemsKey = useMemo(
    () => JSON.stringify(baseMenuItems.map((i) => ({ name: i.name, path: i.path, childCount: i.children?.length ?? 0 }))),
    [baseMenuItems],
  );

  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);

  useEffect(() => {
    const applyFavorites = (favorites: { title: string; to: string; fullPath?: string | null }[]) => {
      const favoritesWithIcons: MenuItem[] = favorites.map((fav) => ({
        name: fav.title,
        path: fav.to,
        icon: reconstructFavoriteIcon(fav, baseMenuItems),
      }));
      setMenuItems(
        baseMenuItems.map((item) => (item.name === 'Favorites' ? { ...item, children: favoritesWithIcons } : item)),
      );
    };

    const cached = getCachedFavorites();
    if (cached.length > 0) applyFavorites(cached);
    else setMenuItems(baseMenuItems);

    const update = async () => {
      try {
        applyFavorites(await getFavorites());
      } catch (e) {
        console.error('Error loading favorites:', e);
      }
    };
    void update();

    const onFavoritesUpdated = () => void update();
    window.addEventListener('favoritesUpdated', onFavoritesUpdated);
    return () => window.removeEventListener('favoritesUpdated', onFavoritesUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseMenuItemsKey]);

  useEffect(() => {
    const onPrefsUpdated = () => void mutateViewPreferences();
    window.addEventListener('viewPreferencesUpdated', onPrefsUpdated);
    return () => window.removeEventListener('viewPreferencesUpdated', onPrefsUpdated);
  }, [mutateViewPreferences]);

  const activeRootName = useMemo(() => {
    const match = menuItems.find((item) => isBranchActive(item, location.pathname, location.search));
    return match?.name ?? null;
  }, [menuItems, location.pathname, location.search]);

  // When pinned, panel follows the active module (if it has panel content)
  useEffect(() => {
    if (!pinned || !activeRootName) return;
    const activeItem = menuItems.find((i) => i.name === activeRootName);
    if (activeItem && hasPanelContent(activeItem)) {
      setOpenSection(activeRootName);
    }
  }, [pinned, activeRootName, menuItems]);

  useEffect(() => {
    if (openSection) localStorage.setItem(OPEN_SECTION_KEY, openSection);
    else localStorage.removeItem(OPEN_SECTION_KEY);
  }, [openSection]);

  const togglePinned = () => {
    setPinned((v) => {
      localStorage.setItem(PANEL_PIN_KEY, String(!v));
      return !v;
    });
  };

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
      if (isMobileOpen) setIsMobileOpen(false);
      if (!pinned) setOpenSection(null);
    },
    [navigate, isMobileOpen, setIsMobileOpen, pinned],
  );

  const handleRailClick = (item: MenuItem) => {
    if (hasPanelContent(item)) {
      setOpenSection((cur) => (cur === item.name ? null : item.name));
      return;
    }
    const target = resolveItemPath(item, viewPreferences);
    if (target) handleNavigate(target);
  };

  const handleViewSelect = async (moduleName: string, view: MenuItemView) => {
    await saveViewPreference(moduleName, view.type, mutateViewPreferences);
    handleNavigate(view.path);
  };

  const openItem = openSection ? menuItems.find((i) => i.name === openSection) ?? null : null;
  const panelVisible = Boolean(openItem && hasPanelContent(openItem));

  const railShell = (
    <div
      className="flex h-full flex-col items-center border-r border-white/[0.08] bg-gradient-to-b from-[#12161d] via-[#0f1319] to-[#0c1016]"
      style={{ width: RAIL_WIDTH, minWidth: RAIL_WIDTH }}
    >
      <div className="flex h-14 w-full shrink-0 items-center justify-center border-b border-white/[0.08]">
        <AppBrandIcon className="h-9 w-9 rounded-xl" iconClassName="h-4 w-4" />
      </div>

      <nav className="rail-nav-scroll flex w-full flex-1 flex-col items-center gap-0.5 overflow-y-auto px-2 py-2">
        {menuItems.map((item) => (
          <RailItem
            key={item.name}
            label={menuLabel(t, item.name)}
            active={activeRootName === item.name}
            open={openSection === item.name && panelVisible}
            badge={item.badge}
            onClick={() => handleRailClick(item)}
          >
            {item.icon}
          </RailItem>
        ))}
      </nav>

      {user && (
        <div className="flex w-full shrink-0 flex-col items-center gap-1 border-t border-white/[0.08] px-2 py-2">
          <button
            type="button"
            onClick={() => handleNavigate('/preferences')}
            title={getUserDisplayName(user as User)}
            aria-label={getUserDisplayName(user as User)}
            className="flex h-10 w-10 items-center justify-center rounded-xl transition-colors hover:bg-white/[0.08]"
          >
            <UserAvatar
              userId={user.id}
              photoPath={user.userPhotoPath}
              userFirstName={user.userFirstName}
              userLastName={user.userLastName}
              userEmail={user.userEmail}
              userDisplayName={user.userDisplayName}
              size="sm"
              rounded="xl"
            />
          </button>
          <button
            type="button"
            onClick={() => void logout()}
            title={t('chrome.signOut')}
            aria-label={t('chrome.signOut')}
            className="group flex h-10 w-10 items-center justify-center rounded-xl transition-colors hover:bg-red-500/15"
          >
            <svg className="h-4 w-4 text-red-400 group-hover:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );

  const panelShell = panelVisible && openItem && (
    <div
      className="flex h-full flex-col border-r border-white/[0.08] bg-[#141922]"
      style={{ width: PANEL_WIDTH, minWidth: PANEL_WIDTH }}
    >
      <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-white/[0.08] px-3">
        <div className="flex min-w-0 items-center gap-2">
          {openItem.icon && (
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-indigo-500/20 text-indigo-200 [&_svg]:h-4 [&_svg]:w-4">
              {openItem.icon}
            </span>
          )}
          <p className="truncate text-sm font-semibold text-white">{menuLabel(t, openItem.name)}</p>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={togglePinned}
            title={pinned ? 'Unpin panel' : 'Pin panel'}
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-lg transition-colors',
              pinned ? 'text-indigo-300 hover:bg-white/10' : 'text-slate-500 hover:bg-white/10 hover:text-slate-300',
            )}
          >
            {pinned ? <Pin className="h-3.5 w-3.5" /> : <PinOff className="h-3.5 w-3.5" />}
          </button>
          <button
            type="button"
            onClick={() => setOpenSection(null)}
            title={t('chrome.closeMenu')}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-white/10 hover:text-slate-300"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-2">
        {openItem.name === 'Favorites' ? (
          <>
            {openItem.children && openItem.children.length > 0 ? (
              <PanelTree
                items={openItem.children}
                depth={0}
                pathname={location.pathname}
                search={location.search}
                viewPreferences={viewPreferences}
                onNavigate={handleNavigate}
              />
            ) : (
              <p className="px-2.5 py-3 text-xs text-slate-500">No favorites yet.</p>
            )}
            <button
              type="button"
              onClick={() => setFavoritesManagerOpen(true)}
              className="mt-2 flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-slate-400 transition-colors hover:bg-white/[0.06] hover:text-slate-100"
            >
              <Settings2 className="h-4 w-4 shrink-0 opacity-80" />
              <span>{t('chrome.manageFavorites')}</span>
            </button>
          </>
        ) : (
          <>
            {openItem.children && openItem.children.length > 0 && (
              <PanelTree
                items={openItem.children}
                depth={0}
                pathname={location.pathname}
                search={location.search}
                viewPreferences={viewPreferences}
                onNavigate={handleNavigate}
              />
            )}
            {openItem.views && openItem.views.length > 1 && (
              <PanelViews
                moduleName={openItem.name}
                views={openItem.views}
                pathname={location.pathname}
                onSelect={(view) => void handleViewSelect(openItem.name, view)}
              />
            )}
          </>
        )}
      </div>

      <div className="shrink-0 border-t border-white/[0.08] px-3 py-2">
        <p className="truncate text-[10px] font-medium uppercase tracking-wider text-slate-600">
          {appConfig.system.appName?.trim() || APP_BRAND.name}
        </p>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 lg:hidden"
          style={zStyle(Z_INDEX.mobileSidebarOverlay)}
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 flex transform transition-transform duration-300 ease-in-out lg:static lg:translate-x-0',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        style={zStyle(Z_INDEX.shellSidebar)}
        aria-label="Main navigation"
      >
        {railShell}
        {panelShell}
      </aside>

      <FavoritesManager open={favoritesManagerOpen} onClose={() => setFavoritesManagerOpen(false)} />
    </>
  );
};

export default TwoTierSidebar;
